import type {
  Product,
  VariantAttribute,
  VariantAttributeOption,
  VariantAttributeValue,
  ProductMedia,
} from "@/types/product";

import {
  createId,
  createImageUrl,
  createPastTimestamp,
  createPrice,
  pickRandom,
  randomBool,
  randomInt,
} from "./base";

interface ProductConfig {
  name: string;
  description: string;
  priceRange: [number, number];
}

// Dental/clinic products for clinics, or general products for e-commerce
const CLINIC_PRODUCTS: ProductConfig[] = [
  {
    name: "Electric Toothbrush Pro",
    description: "Advanced sonic electric toothbrush with 5 cleaning modes",
    priceRange: [89, 149],
  },
  {
    name: "Teeth Whitening Kit",
    description: "Professional-grade teeth whitening system for home use",
    priceRange: [49, 99],
  },
  {
    name: "Dental Floss Pack",
    description: "Premium dental floss, pack of 6",
    priceRange: [12, 24],
  },
  {
    name: "Mouthwash - Mint Fresh",
    description: "Antibacterial mouthwash for fresh breath and healthy gums",
    priceRange: [8, 18],
  },
  {
    name: "Orthodontic Wax",
    description: "Relief wax for braces and orthodontic appliances",
    priceRange: [5, 12],
  },
  {
    name: "Water Flosser",
    description: "Cordless water flosser for deep cleaning between teeth",
    priceRange: [59, 129],
  },
  {
    name: "Sensitive Toothpaste",
    description: "Gentle toothpaste for sensitive teeth, pack of 3",
    priceRange: [18, 32],
  },
  {
    name: "Dental Night Guard",
    description: "Custom-fit night guard to prevent teeth grinding",
    priceRange: [29, 79],
  },
  {
    name: "Tongue Scraper Set",
    description: "Stainless steel tongue scrapers, set of 2",
    priceRange: [8, 15],
  },
  {
    name: "Dental Mirror Kit",
    description: "Professional dental mirror and pick set for home use",
    priceRange: [12, 25],
  },
];

const ATTRIBUTE_CONFIGS = [
  {
    name: "Size",
    options: ["Small", "Medium", "Large"],
  },
  {
    name: "Color",
    options: ["White", "Blue", "Black", "Pink"],
  },
  {
    name: "Material",
    options: ["Plastic", "Stainless Steel", "Silicone"],
  },
];

/**
 * Generate variant attributes
 */
export function generateVariantAttributes(
  organizationId: string,
  workspaceId: string,
): VariantAttribute[] {
  const attributes: VariantAttribute[] = [];
  const timestamp = createPastTimestamp(60);

  for (const config of ATTRIBUTE_CONFIGS) {
    const attributeId = createId();
    const options: VariantAttributeOption[] = config.options.map((opt) => ({
      id: createId(),
      name: opt,
      value: opt.toLowerCase().replace(/\s/g, "_"),
      attributeId,
      createdAt: timestamp,
      updatedAt: null,
    }));

    attributes.push({
      id: attributeId,
      name: config.name,
      description: `Variant ${config.name.toLowerCase()} attribute`,
      options,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: null,
    });
  }

  return attributes;
}

/**
 * Generate mock products
 */
export function generateProducts(
  organizationId: string,
  workspaceId: string,
  attributes: VariantAttribute[],
  count: number = 25,
): Product[] {
  const products: Product[] = [];

  for (let i = 0; i < count; i++) {
    const config = CLINIC_PRODUCTS[i % CLINIC_PRODUCTS.length];
    const timestamp = createPastTimestamp(randomInt(1, 60));
    const productId = createId();

    // Generate 1-4 media items
    const photoCount = randomInt(1, 4);
    const media: ProductMedia[] = Array.from(
      { length: photoCount },
      (_, idx) => {
        const url = createImageUrl(500, 500);

        return {
          type: "image" as const,
          url,
          name: `${config.name.toLowerCase().replace(/\s+/g, "-")}-${idx + 1}.jpg`,
        };
      },
    );

    // Generate attribute values for some products
    const variantAttributes: VariantAttributeValue[] = [];

    if (randomBool(0.7)) {
      const sizeAttr = attributes.find((a) => a.name === "Size");

      if (sizeAttr && sizeAttr.options && sizeAttr.options.length > 0) {
        const option = pickRandom(sizeAttr.options);

        variantAttributes.push({
          id: createId(),
          value: null,
          attribute: {
            id: sizeAttr.id,
            name: sizeAttr.name,
            description: sizeAttr.description,
          },
          option: {
            id: option.id,
            name: option.name,
            value: option.value,
          },
          createdAt: timestamp,
        });
      }
    }

    // Generate prices: cost price < selling price <= list price (for discount display)
    const listPrice = createPrice(config.priceRange[0], config.priceRange[1]);
    const listPriceNum = parseFloat(listPrice);
    const hasDiscount = randomBool(0.4);
    const sellingPrice = hasDiscount
      ? createPrice(listPriceNum * 0.7, listPriceNum * 0.95)
      : listPrice;
    const costPrice = createPrice(listPriceNum * 0.3, listPriceNum * 0.6);

    products.push({
      id: productId,
      name: config.name,
      description: config.description,
      costPrice,
      listPrice,
      sellingPrice,
      media,
      stockCount: randomInt(0, 100),
      sku: `SKU-${String(i + 1).padStart(4, "0")}`,
      barcode: `${String(i + 1).padStart(12, "0")}`,
      parentId: null,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: null,
      deletedAt: null,
      attributes: variantAttributes,
      tags: [],
      weight: randomBool(0.8) ? randomInt(1, 100) / 10 : null,
    });
  }

  return products;
}
